// ============================================================
// H5 端布局：顶部导航 + 底部 TabBar — 深色科技风
// ============================================================

import { useEffect } from 'react'
import { useNavigate, useLocation, Outlet } from 'react-router-dom'
import {
  UserOutlined,
  BellOutlined,
  HomeOutlined,
  ShopOutlined,
  LoginOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons'
import { Badge, Button } from 'antd'
import UserAvatar from '../components/UserAvatar'
import type { ReactNode } from 'react'
import { useAuthStore } from '../stores/auth.store'
import { useNotificationStore } from '../stores/notification.store'
import { triggerLoginModal } from '../components/LoginModal'
import './h5-layout.css'

// ─── TabBar 配置 ────────────────────────────────────────────

interface TabItem {
  key: string
  label: string
  icon: ReactNode
}

const tabs: TabItem[] = [
  { key: '/', label: '首页', icon: <HomeOutlined /> },
  { key: '/shop', label: '商城', icon: <ShopOutlined /> },
  { key: '/my', label: '我的', icon: <UserOutlined /> },
]

// ─── 页面标题映射 ────────────────────────────────────────────

const pageTitleMap: Record<string, string> = {
  '/my/profile': '个人资料',
  '/my/published': '我发布的任务',
  '/my/accepted': '我接取的任务',
  '/my/points': '积分明细',
  '/my/recharge': '积分充值',
  '/my/withdraw': '积分提现',
  '/my/membership': '会员中心',
  '/my/invite': '邀请好友',
  '/my/orders': '兑换订单',
  '/notifications': '消息通知',
  '/tasks': '任务详情',
}

// ─── TabBar 组件 ────────────────────────────────────────────

function H5TabBar() {
  const navigate = useNavigate()
  const location = useLocation()

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname.startsWith(path)
  }

  return (
    <nav className="h5-tabbar">
      {tabs.map((tab) => {
        const active = isActive(tab.key)
        return (
          <button
            key={tab.key}
            className={`h5-tabbar-item ${active ? 'h5-tabbar-item--active' : ''}`}
            onClick={() => navigate(tab.key)}
          >
            <span className="h5-tabbar-icon">{tab.icon}</span>
            <span className="h5-tabbar-label">{tab.label}</span>
          </button>
        )
      })}
    </nav>
  )
}

// ─── H5 Header ─────────────────────────────────────────────

function H5Header() {
  const navigate = useNavigate()
  const location = useLocation()
  const { isLoggedIn, user } = useAuthStore()

  // 判断是否是子页面（需要显示返回按钮和标题）
  const isSubPage = location.pathname !== '/' && location.pathname !== '/shop' && location.pathname !== '/my'

  // 获取页面标题
  const getPageTitle = () => {
    // 精确匹配
    if (pageTitleMap[location.pathname]) {
      return pageTitleMap[location.pathname]
    }
    // 任务详情页特殊处理
    if (location.pathname.startsWith('/tasks/')) {
      return '任务详情'
    }
    return ''
  }

  // 子页面 Header
  if (isSubPage) {
    const pageTitle = getPageTitle()
    return (
      <header className="h5-header">
        <div className="h5-header-left" onClick={() => navigate(-1)} style={{ cursor: 'pointer' }}>
          <ArrowLeftOutlined style={{ color: '#FFFFFF', fontSize: 18 }} />
        </div>
        <div style={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          color: '#FFFFFF',
          fontSize: 16,
          fontWeight: 600,
        }}>
          {pageTitle}
        </div>
        <div className="h5-header-right" style={{ width: 40 }} />
      </header>
    )
  }

  // 主页 Header
  return (
    <header className="h5-header">
      <div className="h5-header-left" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
        <img
          src="/logo.svg"
          alt="广发圩"
          style={{ width: 28, height: 28, borderRadius: 6 }}
        />
        <span className="h5-header-title">广发圩</span>
      </div>
      <div className="h5-header-right">
        {isLoggedIn ? (
          <>
            <Badge count={0} size="small">
              <BellOutlined
                className="text-lg cursor-pointer"
                style={{ color: '#A0A0B0' }}
                onClick={() => navigate('/notifications')}
              />
            </Badge>
            <div onClick={() => navigate('/my')} className="cursor-pointer">
              <UserAvatar avatar={user?.avatar} size={28} />
            </div>
          </>
        ) : (
          <Button
            type="primary"
            size="small"
            icon={<LoginOutlined />}
            onClick={() => triggerLoginModal()}
            style={{
              background: 'linear-gradient(135deg, #8B5CF6, #EC4899)',
              border: 'none',
              borderRadius: 8,
              height: 28,
              padding: '0 10px',
              fontSize: 12,
            }}
          >
            登录
          </Button>
        )}
      </div>
    </header>
  )
}

// ─── H5 布局主组件 ──────────────────────────────────────────

export default function H5Layout() {
  const { isLoggedIn } = useAuthStore()
  const { startPolling, stopPolling } = useNotificationStore()

  // 登录后启动通知轮询
  useEffect(() => {
    if (isLoggedIn) {
      startPolling()
    } else {
      stopPolling()
    }
    return () => stopPolling()
  }, [isLoggedIn])
  return (
    <div className="h5-container">
      <H5Header />
      <main className="h5-content">
        <Outlet />
      </main>
      <H5TabBar />
    </div>
  )
}
